import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, ArrowLeft, Shield, FileText, Users, Scale } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const AgentTerms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <h1 className="text-3xl font-bold mb-2">Agent / NIL Tools Subscriber Terms</h1>
        <p className="text-muted-foreground mb-8">
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>

        {/* Critical Disclaimer */}
        <Alert className="mb-8 border-2 border-yellow-500/50 bg-yellow-500/10">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <AlertTitle className="text-lg font-bold text-yellow-700">
            Platform Role Clarification
          </AlertTitle>
          <AlertDescription className="text-yellow-700 mt-2">
            <p className="font-bold text-lg mb-2">
              Players Lockeroom provides software tools designed to support athlete discovery, 
              organization, and communication. Players Lockeroom does not act as an agent, 
              representative, broker, or intermediary for athletes, teams, or organizations.
            </p>
          </AlertDescription>
        </Alert>

        {/* No Representation */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              No Representation or Negotiation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>Players Lockeroom expressly disclaims any role as a representative or agent. Specifically:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Does not negotiate NIL deals</strong> on behalf of any user</li>
              <li><strong>Does not solicit agreements</strong> on behalf of users</li>
              <li><strong>Does not receive compensation</strong> tied to NIL earnings or deal outcomes</li>
              <li><strong>Does not provide legal, financial, or contract advice</strong></li>
            </ul>
            <p className="font-medium mt-4">
              All communications, agreements, and transactions occur solely between users. 
              Players Lockeroom is not a party to any agreement made through use of the platform.
            </p>
          </CardContent>
        </Card>

        {/* Founding Agent Beta Terms */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Founding Agent Beta Access Terms
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              Founding Agent Beta Access is a limited early-access program offered to the 
              first 200 approved Agent / NIL Tools subscribers.
            </p>
            
            <h4 className="font-semibold mt-4">Founding Agent Benefits Include:</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li>24 months of platform access for the price of one annual subscription</li>
              <li>Lifetime price lock at the founding rate (as long as subscription remains active)</li>
              <li>Founding Agent badge displayed within the platform</li>
              <li>Priority access to new NIL and agent tools as they are released</li>
              <li>Early feature feedback and roadmap influence opportunities</li>
              <li>Priority support during the beta phase</li>
            </ul>

            <Alert className="mt-6">
              <AlertDescription>
                <strong>Important:</strong> Founding Agent Beta Access benefits, including extended 
                access and pricing protection, are offered in recognition of early platform support. 
                Features may be released incrementally, modified, or expanded during the beta period. 
                Players Lockeroom makes no guarantees regarding specific feature delivery timelines 
                during beta.
              </AlertDescription>
            </Alert>

            <h4 className="font-semibold mt-6">Cancellation and Renewal</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li>Founding Agent status and price lock remain active as long as the subscription is continuously maintained</li>
              <li>If a Founding Agent cancels their subscription, Founding Agent status is permanently forfeited</li>
              <li>Re-entry after cancellation requires subscribing at current public pricing</li>
              <li>Renewal occurs automatically at the locked founding rate</li>
            </ul>
          </CardContent>
        </Card>

        {/* Subscription Terms */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5" />
              Subscription Terms
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h4 className="font-semibold">Billing</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li>Subscriptions are billed monthly or annually based on the selected plan</li>
              <li>Annual subscriptions are billed upfront for the full year</li>
              <li>Prices are subject to change; existing subscribers will be notified in advance of any changes</li>
            </ul>

            <h4 className="font-semibold mt-4">Contact Limits</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li>Monthly contact limits reset on the first of each calendar month</li>
              <li>Unused contacts do not roll over to the following month</li>
              <li>Contact limits apply to message sends and athlete saves</li>
            </ul>

            <h4 className="font-semibold mt-4">Refunds</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li>Monthly subscriptions: No refunds for partial months</li>
              <li>Annual subscriptions: Pro-rated refunds may be available within 30 days of purchase</li>
              <li>Founding Agent extended access periods are non-refundable</li>
            </ul>
          </CardContent>
        </Card>

        {/* Minors & Compliance */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Minors & Compliance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              Users are responsible for complying with all applicable federal, state, and 
              institutional rules regarding NIL activities, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Parental consent requirements for minor athletes</li>
              <li>Age-based restrictions on NIL agreements</li>
              <li>NCAA, NAIA, or other athletic association regulations</li>
              <li>State-specific NIL laws and disclosure requirements</li>
            </ul>
            <p className="font-medium mt-4">
              Players Lockeroom provides tools only and does not verify NIL eligibility, 
              compliance status, or legal authority of any user.
            </p>
          </CardContent>
        </Card>

        {/* Acceptable Use */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Acceptable Use</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>Agent / NIL Tools Subscribers agree to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the platform only for legitimate professional purposes</li>
              <li>Not misrepresent their relationship with Players Lockeroom</li>
              <li>Not use the platform to harass, spam, or inappropriately contact users</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Maintain accurate profile and company information</li>
              <li>Respect athlete privacy and data protection requirements</li>
            </ul>
            <p className="mt-4">
              Violation of these terms may result in immediate suspension or termination 
              of the subscription without refund.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Questions?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              If you have questions about these terms or your subscription, please contact 
              our support team.
            </p>
            <Button onClick={() => navigate('/support')}>
              Contact Support
            </Button>
          </CardContent>
        </Card>

        <Separator className="my-8" />

        <p className="text-center text-sm text-muted-foreground">
          By subscribing to Agent / NIL Tools, you acknowledge that you have read, 
          understood, and agree to these terms.
        </p>
      </main>

      <Footer />
    </div>
  );
};

export default AgentTerms;
